/*
  REFORGED — shared motion + announcement utilities.

  Deliberately tiny and dependency-free. Anything component-specific belongs
  in that component's own module, loaded by the section that renders it.
*/

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Scroll reveal ------------------------------------------- */

/*
  Elements carrying .rf-reveal start hidden in CSS and are revealed once.
  The `no-js` fallback in base.css means content is always visible if this
  module fails to load — the animation is an enhancement, never a gate.

  One observer for the life of the page, not one per call: initReveal runs
  again every time content is injected, and a fresh IntersectionObserver each
  time would pile up. Observing an element twice is a no-op, so re-running is
  safe.
*/
let revealObserver = null;

function getRevealObserver() {
  if (revealObserver) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
  );

  return revealObserver;
}

export function initReveal() {
  const targets = document.querySelectorAll('.rf-reveal:not(.is-visible)');
  if (!targets.length) return;

  if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = getRevealObserver();
  targets.forEach((el) => observer.observe(el));
}

/*
  Anything injected after load — facet results, product recommendations,
  predictive search, the cart drawer — arrives with .rf-reveal still at
  opacity 0 and nothing watching it. It then stays invisible until the page is
  refreshed, which is exactly what a customer clicking a filter saw: the URL
  changed, the products were in the DOM, and none of them could be seen.

  Watching the tree costs less than remembering to call initReveal from every
  module that swaps innerHTML, and it cannot be forgotten by the next one.
  Only childList is observed: initReveal adds a class, which is an attribute
  mutation, so this cannot feed itself.
*/
function watchForInjectedContent() {
  if (!('MutationObserver' in window)) return;

  let queued = false;

  const observer = new MutationObserver((mutations) => {
    if (queued) return;
    if (!mutations.some((m) => m.addedNodes.length > 0)) return;

    // Coalesce: a single innerHTML swap fires many mutations.
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      initReveal();
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

/* ---------- Scroll lock ---------------------------------------------- */

/*
  Drawers and modals share one lock so two open surfaces cannot fight over
  the body scrollbar. Compensating for the scrollbar width stops the layout
  shift you otherwise get on desktop when the bar disappears.
*/
let lockCount = 0;

export function lockScroll() {
  if (lockCount === 0) {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('padding-inline-end', `${gap}px`);
    document.body.classList.add('rf-scroll-locked');
  }
  lockCount += 1;
}

export function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.classList.remove('rf-scroll-locked');
    document.body.style.removeProperty('padding-inline-end');
  }
}

/* ---------- Live region ----------------------------------------------- */

/*
  Single polite live region in the layout. Cart updates, filter counts, and
  form results announce through here so screen readers hear async changes.
*/
export function announce(message) {
  const region = document.getElementById('RfLiveRegion');
  if (!region) return;
  // Clearing first forces re-announcement of an identical message.
  region.textContent = '';
  window.requestAnimationFrame(() => {
    region.textContent = message;
  });
}

/* ---------- Money ------------------------------------------------------ */

/*
  The subunit factor is 1000 for TND, not the 100 every theme assumes.
  Read it from the body dataset rather than hardcoding a divisor anywhere.
*/
export function subunitFactor() {
  return Number(document.body.dataset.moneySubunitFactor) || 100;
}

export function toMajorUnits(subunits) {
  return subunits / subunitFactor();
}

/* ---------- Boot -------------------------------------------------------- */

initReveal();
watchForInjectedContent();

// Theme editor re-renders sections without a page load.
document.addEventListener('shopify:section:load', initReveal);
document.addEventListener('shopify:section:unload', initReveal);
